import type { VideoModel } from '..';
import {
    BaseVideoProvider,
    type VideoProviderAdapter,
    TaskStatus,
    type CreateTaskConfig,
    type CreateTaskResult,
    type PollTaskConfig,
    type PollTaskResult,
} from '../base';
import { buildInputPayloadFromConfig, getcoolModelConfig } from './utils';
import { coolApiPaths, coolStatusMapping } from './config';
import type { coolCreateResponse, coolTaskResponse } from './types';
import { getValueByPath, isStatusMatch } from '../tools';

// 创建提供商实例
const provider = new BaseVideoProvider({
    baseUrlEnv: 'COOL_BASE_URL',
    apiKeyEnv: 'COOL_API_KEY',
    debugEnv: 'COOL_DEBUG',
    providerName: 'COOL',
});


// 实现适配器
const adapter: VideoProviderAdapter = {
    // 构建**任务创建**请求
    async buildCreateTaskRequest(modelId, params, context): Promise<CreateTaskConfig> {
        // 获取模型配置
        const apiPath = coolApiPaths.createTask;
        // 构建输入参数（可能是异步构建）
        const payload = await buildInputPayloadFromConfig(modelId, params);
        // 返回请求配置
        return {
            url: `${context.baseUrl}${apiPath}`,
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${context.apiKey}`,
            },
            body: payload,
        };
    },

    // 解析**任务创建**响应
    parseCreateTaskResponse(modelId, response, data: coolCreateResponse): CreateTaskResult {
        // 获取任务 ID
        const taskId = getValueByPath(data, coolStatusMapping.taskIdPath);
        if (!taskId) {
            throw new Error(`Failed to get task id from response${data.msg ? `: ${data.msg}` : ''}`);
        }
        // 返回任务 ID
        return {
            taskId: taskId,
            raw: data,
        };
    },

    // 构建**轮询任务**请求
    buildPollTaskRequest(modelId, taskId, params, context): PollTaskConfig {
        // 返回请求配置
        return {
            url: (taskId) => `${context.baseUrl}${coolApiPaths.pollTask}/${taskId}`,
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${context.apiKey}`,
            },
        };
    },

    // 解析**轮询任务**响应
    parsePollTaskResponse(modelId, response, data: coolTaskResponse): PollTaskResult {
        // 获取任务状态
        const state = getValueByPath(data, coolStatusMapping.statePath);
        // 如果状态值未找到则视为**处理中**
        if (state === undefined) {
            return {
                status: TaskStatus.PROCESSING,
                raw: data,
            };
        }
        // 1. 判断**成功**状态
        if (isStatusMatch(state, coolStatusMapping.success)) {
            // 直接获取视频 URL
            const videoUrl = getValueByPath(data, coolStatusMapping.videoUrlPath);
            // 视频 URL 不存在或不是字符串则视为**失败**状态
            if (!videoUrl || typeof videoUrl !== 'string') {
                return {
                    status: TaskStatus.FAILED,
                    error: 'Missing video URL in response',
                    raw: data,
                };
            }
            // 返回**成功**状态
            return {
                status: TaskStatus.SUCCESS,
                videoUrl,
                raw: data,
            };
        }

        // 2. 判断**失败**状态
        if (isStatusMatch(state, coolStatusMapping.failed)) {
            const error = getValueByPath(data, coolStatusMapping.errorPath);
            // 返回**失败**状态
            return {
                status: TaskStatus.FAILED,
                error: String(error) || 'unknown error',
                raw: data,
            };
        }

        // 3. 判断**处理中**状态
        if (isStatusMatch(state, coolStatusMapping.processing)) {
            // 返回**处理中**状态
            return {
                status: TaskStatus.PROCESSING,
                raw: data,
            };
        }

        // 4. 判断**未知**状态 (不在配置范围内)
        // 返回**处理中**状态
        return {
            status: TaskStatus.PROCESSING,
            raw: data,
        };
    },
};

// 导出工厂函数
export const cool = (modelId: string): VideoModel => {
    const config = getcoolModelConfig(modelId);
    return provider.createVideoModel(modelId, adapter);
};